import { images } from './site'

export const SITE_URL = 'https://nexlaw.ca'
export const SITE_NAME = 'NexLaw'
export const SITE_TAGLINE = 'The AI-native law firm'
export const CONTACT_EMAIL = 'hello@nexlaw.ca'
/** Placeholder — replace with the real business line before launch. */
export const CONTACT_PHONE = '+1-000-000-0000'
export const DEFAULT_OG_IMAGE = images.hero
export const AREAS_SERVED = ['Ontario', 'Quebec']

export const SOCIAL_PROFILES = [
  'https://www.linkedin.com/company/nexlaw',
  'https://x.com/nexlawca',
]

export type PageSeo = {
  title: string
  description: string
  path: string
  keywords: string[]
}

export const pageSeo: Record<
  'home' | 'howItWorks' | 'services' | 'about' | 'contact',
  PageSeo
> = {
  home: {
    title: 'AI-Native Law Firm in Ontario & Quebec | NexLaw',
    description:
      'NexLaw is an AI-native law firm combining technology, artificial intelligence, and lawyer expertise to deliver modern legal services across Ontario and Quebec.',
    path: '/',
    keywords: [
      'AI-native law firm',
      'modern law firm Canada',
      'legal technology Canada',
      'lawyer services Ontario',
      'lawyer services Quebec',
    ],
  },
  howItWorks: {
    title: 'How NexLaw Works | AI-Powered Legal Services',
    description:
      'Learn how NexLaw combines AI-powered legal workflows with lawyer review to create a faster, clearer, and more accessible legal experience in Canada.',
    path: '/how-it-works',
    keywords: [
      'AI legal services',
      'legal technology',
      'AI lawyer workflow',
      'technology-enabled legal services',
    ],
  },
  services: {
    title: 'Legal Services for Businesses & Individuals | NexLaw',
    description:
      'Explore NexLaw legal services for business, family, property, and disputes — AI-supported, lawyer-reviewed, and available across Ontario and Quebec.',
    path: '/services',
    keywords: [
      'business lawyer Ontario',
      'corporate lawyer Quebec',
      'legal services Canada',
      'fixed-fee legal services',
    ],
  },
  about: {
    title: 'About NexLaw | A Modern AI-Native Law Firm',
    description:
      'NexLaw is a modern AI-native law firm built on clarity, efficiency, and professional oversight, shaping the future of legal services in Ontario and Quebec.',
    path: '/about',
    keywords: [
      'AI law firm Canada',
      'modern legal services',
      'future of legal services',
    ],
  },
  contact: {
    title: 'Contact NexLaw | Modern Legal Services',
    description:
      'Contact NexLaw to learn how our AI-supported, lawyer-reviewed legal services work for individuals and businesses across Ontario and Quebec.',
    path: '/contact',
    keywords: ['contact law firm Ontario', 'contact law firm Quebec', 'NexLaw contact'],
  },
}

export type Faq = {
  question: string
  answer: string
}

export const faqs: Faq[] = [
  {
    question: 'What is an AI-native law firm?',
    answer:
      'An AI-native law firm uses technology and artificial intelligence to improve legal workflows while lawyers provide professional review, advice, and oversight.',
  },
  {
    question: 'Does AI replace lawyers at NexLaw?',
    answer:
      'No. NexLaw uses AI to support lawyers and improve the delivery of legal services.',
  },
  {
    question: 'Where does NexLaw provide services?',
    answer: 'NexLaw is initially focused on Ontario and Quebec.',
  },
]

/**
 * Route registry — the single source of truth for the sitemap, breadcrumbs, and
 * future expansion (service detail pages, resources, lawyer profiles).
 */
export type RouteEntry = {
  path: string
  label: string
  priority: number
  changefreq: 'weekly' | 'monthly' | 'yearly'
  /** Planned but not yet published — excluded from the sitemap. */
  planned?: boolean
}

export const routeRegistry: RouteEntry[] = [
  { path: '/', label: 'Home', priority: 1.0, changefreq: 'weekly' },
  { path: '/how-it-works', label: 'How It Works', priority: 0.9, changefreq: 'monthly' },
  { path: '/services', label: 'Services', priority: 0.9, changefreq: 'monthly' },
  { path: '/about', label: 'About', priority: 0.7, changefreq: 'monthly' },
  { path: '/contact', label: 'Contact', priority: 0.6, changefreq: 'yearly' },
  // Planned expansion — keeps URL structure decided ahead of build-out.
  { path: '/services/incorporation', label: 'Incorporation', priority: 0.8, changefreq: 'monthly', planned: true },
  { path: '/services/wills', label: 'Wills', priority: 0.8, changefreq: 'monthly', planned: true },
  { path: '/services/employment-law', label: 'Employment Law', priority: 0.8, changefreq: 'monthly', planned: true },
  { path: '/resources', label: 'Resources', priority: 0.7, changefreq: 'weekly', planned: true },
  { path: '/resources/legal-guides', label: 'Legal Guides', priority: 0.6, changefreq: 'weekly', planned: true },
]

export const publishedRoutes = routeRegistry.filter((route) => !route.planned)
